"use client"

import { useState, useEffect } from "react"
import { AuthLayout } from "@/components/auth/auth-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useRouter } from "@/src/hooks/useRouter"
import { Eye, EyeOff, ArrowLeft } from "lucide-react"
import Link from "@/src/components/Link"

export default function ForgotPasswordPage() {
  const router = useRouter()
  const [step, setStep] = useState<"phone" | "otp" | "newPassword">("phone")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [countdown, setCountdown] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Form data
  const [phoneNumber, setPhoneNumber] = useState("")
  const [otpCode, setOtpCode] = useState("")
  const [otpId, setOtpId] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [resetToken, setResetToken] = useState("")

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  // Step 1: Request OTP
  const handleRequestOtp = async () => {
    setError("")
    setSuccess("")

    // Validate phone number
    if (!phoneNumber.match(/^0[6-9]\d{8}$/)) {
      setError("กรุณากรอกหมายเลขโทรศัพท์ให้ถูกต้อง (10 หลัก)")
      return
    }

    try {
      setLoading(true)
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/forgot-password/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'เกิดข้อผิดพลาดในการส่ง OTP')
      }

      setOtpId(data.data.otpId)
      setSuccess("ส่ง OTP สำเร็จ กรุณาตรวจสอบข้อความใน SMS")
      setStep("otp")
      setCountdown(300) // 5 minutes
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Step 2: Verify OTP
  const handleVerifyOtp = async () => {
    setError("")
    setSuccess("")

    if (!otpCode || otpCode.length !== 6) {
      setError("กรุณากรอกรหัส OTP 6 หลัก")
      return
    }

    try {
      setLoading(true)
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/forgot-password/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          phoneNumber,
          otpCode,
          otpId 
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'รหัส OTP ไม่ถูกต้อง')
      }

      setResetToken(data.data.resetToken)
      setSuccess("ยืนยัน OTP สำเร็จ กรุณาตั้งรหัสผ่านใหม่")
      setStep("newPassword")
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Step 3: Change Password
  const handleChangePassword = async () => {
    setError("")
    setSuccess("")

    // Validate passwords
    if (!newPassword || !confirmPassword) {
      setError("กรุณากรอกรหัสผ่านใหม่และยืนยันรหัสผ่าน")
      return
    }

    if (newPassword !== confirmPassword) {
      setError("รหัสผ่านใหม่และยืนยันรหัสผ่านไม่ตรงกัน")
      return
    }

    if (newPassword.length < 6) {
      setError("รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร")
      return
    }

    try {
      setLoading(true)
      
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          phoneNumber,
          newPassword,
          confirmPassword,
          resetToken // Use resetToken from OTP verification
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'ไม่สามารถเปลี่ยนรหัสผ่านได้')
      }

      setSuccess("เปลี่ยนรหัสผ่านสำเร็จ กำลังนำทางไปหน้าเข้าสู่ระบบ...")
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        router.push("/auth/login")
      }, 2000)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Resend OTP
  const handleResendOtp = async () => {
    if (countdown > 0) return
    await handleRequestOtp()
  }

  const renderPhoneStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-[#323130] mb-2">ลืมรหัสผ่าน</h1>
        <p className="text-[#605e5c] text-sm">
          กรุณากรอกเบอร์โทรศัพท์ที่ใช้ในการสมัครสมาชิก
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
          {success}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="phoneNumber" className="text-[#323130] font-medium">
          เบอร์โทรศัพท์
        </Label>
        <Input
          id="phoneNumber"
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="กรอกหมายเลขโทรศัพท์"
          className="bg-white border-[#d1d1d1] text-[#323130]"
          maxLength={10}
        />
      </div>

      <Button 
        onClick={handleRequestOtp}
        className="w-full bg-[#0078d4] hover:bg-[#106ebe] text-white font-medium py-3"
        disabled={loading || !phoneNumber}
      >
        {loading ? "กำลังส่ง OTP..." : "ส่ง OTP"}
      </Button>

      <div className="text-center">
        <Link 
          href="/auth/login" 
          className="text-[#0078d4] hover:underline text-sm inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          กลับไปหน้าเข้าสู่ระบบ
        </Link>
      </div>
    </div>
  )

  const renderOtpStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-[#323130] mb-2">ยืนยัน OTP</h1>
        <p className="text-[#605e5c] text-sm">
          กรุณากรอกรหัส OTP ที่ส่งไปยัง {phoneNumber}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
          {success}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="otpCode" className="text-[#323130] font-medium">
          รหัส OTP
        </Label>
        <Input
          id="otpCode"
          type="text"
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="กรอกรหัส OTP 6 หลัก"
          className="bg-white border-[#d1d1d1] text-[#323130] text-center text-xl tracking-widest"
          maxLength={6}
        />
      </div>

      <div className="text-center text-sm text-[#605e5c]">
        {countdown > 0 ? (
          <p>ส่ง OTP อีกครั้งได้ใน {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}</p>
        ) : (
          <button 
            onClick={handleResendOtp}
            className="text-[#0078d4] hover:underline"
          >
            ส่ง OTP อีกครั้ง
          </button>
        )}
      </div>

      <Button 
        onClick={handleVerifyOtp}
        className="w-full bg-[#0078d4] hover:bg-[#106ebe] text-white font-medium py-3"
        disabled={loading || otpCode.length !== 6}
      >
        {loading ? "กำลังยืนยัน..." : "ยืนยัน OTP"}
      </Button>

      <div className="text-center">
        <button 
          onClick={() => setStep("phone")}
          className="text-[#0078d4] hover:underline text-sm inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          เปลี่ยนเบอร์โทรศัพท์
        </button>
      </div>
    </div>
  )

  const renderNewPasswordStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-[#323130] mb-2">ตั้งรหัสผ่านใหม่</h1>
        <p className="text-[#605e5c] text-sm">
          กรุณาตั้งรหัสผ่านใหม่สำหรับบัญชี {phoneNumber}
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
          {success}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="newPassword" className="text-[#323130] font-medium">
          รหัสผ่านใหม่
        </Label>
        <div className="relative">
          <Input
            id="newPassword"
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="กรอกรหัสผ่านใหม่"
            className="bg-white border-[#d1d1d1] text-[#323130] pr-10"
            minLength={6}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#605e5c] hover:text-[#323130]"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <p className="text-xs text-[#605e5c]">รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword" className="text-[#323130] font-medium">
          ยืนยันรหัสผ่านใหม่
        </Label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="ยืนยันรหัสผ่านใหม่"
            className="bg-white border-[#d1d1d1] text-[#323130] pr-10"
            minLength={6}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#605e5c] hover:text-[#323130]"
          >
            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <Button 
        onClick={handleChangePassword}
        className="w-full bg-[#0078d4] hover:bg-[#106ebe] text-white font-medium py-3"
        disabled={loading || !newPassword || !confirmPassword}
      >
        {loading ? "กำลังเปลี่ยนรหัสผ่าน..." : "เปลี่ยนรหัสผ่าน"}
      </Button>
    </div>
  )

  return (
    <AuthLayout>
      <div className="bg-[#e4ffe5] rounded-2xl shadow-xl p-8">
        {step === "phone" && renderPhoneStep()}
        {step === "otp" && renderOtpStep()}
        {step === "newPassword" && renderNewPasswordStep()}
      </div>
    </AuthLayout>
  )
}